import { Link } from "react-router-dom";
import { AlertTriangle, DollarSign, Pencil, Plus } from "lucide-react";

import { Button, buttonVariants } from "@/components/ui/button";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { cn } from "@/lib/utils";
import {
    compliancePill,
    devicesLabel,
    filingLabel,
    lastVisitLabel,
    moneyLabel,
    openWorkLabel,
    type ComplianceTone,
    type SiteRollup,
} from "@/lib/site-rollup";

/**
 * The Locations tab's book-of-business table (#992).
 *
 * Hand-rolled rather than `DataTable` for one reason: `DataTable` sorts the
 * rows it was handed, and this list is paged server-side. Sorting a 50-row
 * page and labelling the header "sorted by overdue" would show the worst of
 * page one, not the worst of the account — a plausible-looking answer to a
 * question nobody asked.
 */

export interface LocationSite {
    id: string;
    name: string | null;
    address_line: string | null;
    address_line2: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
    water_provider_id: string | null;
    water_provider_name?: string | null;
    water_provider_submission_platform?: string | null;
    rollup?: SiteRollup | null;
}

export const LOCATION_SORT_KEYS = [
    "location",
    "compliance",
    "devices",
    "last_visit",
    "open_work",
    "money",
    "filing",
] as const;
export type LocationSortKey = (typeof LOCATION_SORT_KEYS)[number];

export interface SiteBucketChip {
    id: string;
    label: string;
    help: string;
    count: number;
}

/**
 * Named worklists across the top of the tab.
 *
 * Counts come from the server's one rollup of the whole filtered account —
 * never from summing the visible page, which would read "3 overdue" on an
 * account that has two hundred.
 */
export function BucketStrip({
    buckets,
    active,
    onSelect,
    loading,
}: {
    buckets: SiteBucketChip[];
    active: string;
    onSelect: (id: string) => void;
    loading: boolean;
}) {
    if (loading) {
        return <div className="mb-3 h-8 w-full animate-pulse rounded bg-muted" aria-hidden />;
    }
    return (
        <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Filter locations">
            {buckets.map((b) => {
                const selected = b.id === active;
                // A bucket at zero stays visible and reads as zero. Hiding it
                // would make "no overdue locations" and "we didn't check"
                // look the same, which is the bug this whole tab is about.
                const empty = b.count === 0 && b.id !== "all";
                return (
                    <button
                        key={b.id}
                        type="button"
                        title={b.help}
                        aria-pressed={selected}
                        onClick={() => onSelect(b.id)}
                        className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                            // Every tone pins BOTH a surface and a foreground:
                            // these chips render on light cards and on the dark
                            // shell, and a tone that inherits its text colour
                            // disappears on one of them.
                            selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : empty
                                  ? "border-border bg-muted text-muted-foreground hover:text-foreground"
                                  : "border-border bg-card text-card-foreground hover:border-primary/60",
                        )}
                    >
                        {b.label}
                        <span
                            className={cn(
                                "rounded px-1 tabular-nums",
                                selected ? "bg-primary-foreground/20" : "bg-muted text-foreground",
                            )}
                        >
                            {b.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

const COMPLIANCE_TONE_CLASS: Record<ComplianceTone, string> = {
    overdue: "border-transparent bg-destructive text-destructive-foreground",
    current: "border-transparent bg-success text-success-foreground",
    unsurveyed: "border-border bg-warning text-warning-foreground",
    no_devices: "border-border bg-muted text-foreground",
};

function CompliancePill({ rollup }: { rollup: SiteRollup | null | undefined }) {
    const pill = compliancePill(rollup);
    return (
        <div className="space-y-0.5">
            <span
                className={cn(
                    "inline-flex w-fit items-center rounded-md border px-1.5 py-0.5 text-xs font-semibold",
                    COMPLIANCE_TONE_CLASS[pill.tone],
                )}
            >
                {pill.label}
            </span>
            {pill.detail && <div className="text-xs text-foreground/70">{pill.detail}</div>}
        </div>
    );
}

interface HeaderSpec {
    key: LocationSortKey;
    label: string;
    className?: string;
}

const HEADERS: HeaderSpec[] = [
    { key: "location", label: "Location" },
    { key: "compliance", label: "Compliance" },
    { key: "devices", label: "Devices" },
    { key: "last_visit", label: "Last visit" },
    { key: "open_work", label: "Open work" },
    { key: "money", label: "Money" },
    { key: "filing", label: "Filing" },
];

export function LocationsTable({
    sites,
    customerId,
    sort,
    dir,
    onSort,
    onEdit,
    canPriceCheck,
    onPriceCheck,
    loading,
    emptyMessage,
    selected,
    onToggleSelect,
    onToggleAll,
}: {
    sites: LocationSite[];
    customerId: string;
    sort: LocationSortKey;
    dir: "asc" | "desc";
    onSort: (key: LocationSortKey) => void;
    onEdit: (siteId: string) => void;
    canPriceCheck: boolean;
    onPriceCheck: (providerId: string, providerName?: string) => void;
    loading: boolean;
    emptyMessage: string;
    /** #1039. Omit to render the table without selection at all. */
    selected?: ReadonlySet<string>;
    onToggleSelect?: (siteId: string) => void;
    onToggleAll?: (siteIds: string[]) => void;
}) {
    const selectable = !!selected && !!onToggleSelect && !!onToggleAll;
    const allShown = selectable && sites.length > 0 && sites.every((x) => selected!.has(x.id));
    if (loading && sites.length === 0) {
        return <div className="h-32 w-full animate-pulse rounded bg-muted" aria-hidden />;
    }
    if (sites.length === 0) {
        return (
            <div className="rounded-md border border-border p-6 text-center text-sm text-foreground/80">
                {emptyMessage}
            </div>
        );
    }

    return (
        // The table can outgrow a phone; it scrolls INSIDE this box so the page
        // body never scrolls sideways.
        <div className="overflow-x-auto">
            <table className="w-full min-w-[64rem] text-sm">
                <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-foreground/70">
                        {selectable && (
                            <th className="w-8 px-2 py-2">
                                <input
                                    type="checkbox"
                                    checked={allShown}
                                    onChange={() => onToggleAll!(sites.map((x) => x.id))}
                                    aria-label="Select all shown"
                                    className="h-4 w-4"
                                />
                            </th>
                        )}
                        {HEADERS.map((h) => (
                            <th key={h.key} className={cn("px-2 py-2 font-medium", h.className)}>
                                <SortHeaderButton
                                    label={h.label}
                                    active={sort === h.key}
                                    dir={dir}
                                    onClick={() => onSort(h.key)}
                                />
                            </th>
                        ))}
                        <th className="px-2 py-2" />
                    </tr>
                </thead>
                <tbody>
                    {sites.map((s) => {
                        const r = s.rollup;
                        const visit = lastVisitLabel(r);
                        const work = openWorkLabel(r);
                        const money = moneyLabel(r);
                        const filing = filingLabel(r, s);
                        return (
                            <tr key={s.id} className="border-b border-border/60 align-top hover:bg-muted/40">
                                {selectable && (
                                    <td className="px-2 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selected!.has(s.id)}
                                            onChange={() => onToggleSelect!(s.id)}
                                            aria-label={`Select ${s.name ?? "location"}`}
                                            className="h-4 w-4"
                                        />
                                    </td>
                                )}
                                <td className="px-2 py-2">
                                    <Link to={`/sites/${s.id}`} className="font-medium text-primary hover:underline">
                                        {s.name ?? "(unnamed)"}
                                    </Link>
                                    <div className="text-xs text-foreground/80">
                                        {formatSiteAddress(s) || "No address on file"}
                                    </div>
                                </td>
                                <td className="px-2 py-2">
                                    <CompliancePill rollup={r} />
                                </td>
                                <td className="px-2 py-2">
                                    <span className={cn("text-sm", r?.device_count ? "" : "text-foreground/70 italic")}>
                                        {devicesLabel(r)}
                                    </span>
                                    {r && r.missing_due_count > 0 && (
                                        // `text-warning`, NOT
                                        // `text-warning-foreground`: the
                                        // -foreground token is the colour that
                                        // sits ON a solid warning surface
                                        // (white in light mode, near-black in
                                        // dark). As bare text on a card it is
                                        // invisible in BOTH themes. Caught by
                                        // reading the 1560px render proof.
                                        <div className="text-xs font-medium text-warning">
                                            {r.missing_due_count} with no due date
                                        </div>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    <span className={cn("text-sm", visit.never && "text-foreground/70 italic")}>
                                        {visit.text}
                                    </span>
                                    {visit.qualifier && (
                                        <div className="text-xs text-foreground/60">{visit.qualifier}</div>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    <span className={cn("text-sm", work.idle && "text-foreground/70 italic")}>
                                        {work.text}
                                    </span>
                                    {work.warning && (
                                        <div className="flex items-center gap-1 text-xs text-destructive">
                                            <AlertTriangle className="h-3 w-3 shrink-0" />
                                            {work.warning}
                                        </div>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    {money.empty ? (
                                        <span className="text-sm text-foreground/70 italic">Nothing owed</span>
                                    ) : (
                                        <div className="space-y-0.5">
                                            {money.owed && (
                                                <div className="text-sm font-medium text-destructive">
                                                    {money.owed} owed
                                                </div>
                                            )}
                                            {money.approved && (
                                                <div className="text-xs font-medium text-warning">
                                                    {money.approved} approved, not scheduled
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    <div className="flex items-center gap-1">
                                        <span
                                            className={cn(
                                                "text-sm",
                                                filing.blocked
                                                    ? "rounded border border-transparent bg-destructive px-1.5 py-0.5 text-xs font-semibold text-destructive-foreground"
                                                    : !s.water_provider_name && "text-foreground/70 italic",
                                            )}
                                        >
                                            {filing.text}
                                        </span>
                                        {s.water_provider_id && canPriceCheck && (
                                            <button
                                                type="button"
                                                title="Price check — what a backflow test costs here"
                                                aria-label={`Price check for ${s.water_provider_name ?? "this purveyor"}`}
                                                onClick={() =>
                                                    onPriceCheck(
                                                        s.water_provider_id!,
                                                        s.water_provider_name ?? undefined,
                                                    )
                                                }
                                                className="rounded p-0.5 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"
                                            >
                                                <DollarSign className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    {s.water_provider_name && filing.text !== s.water_provider_name && (
                                        <div className="text-xs text-foreground/70">{s.water_provider_name}</div>
                                    )}
                                </td>
                                <td className="px-2 py-2">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <Button size="sm" variant="outline" onClick={() => onEdit(s.id)}>
                                            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                                        </Button>
                                        <Link
                                            to={`/jobs/new?customerId=${customerId}&siteId=${s.id}`}
                                            className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                                        >
                                            <Plus className="mr-1 h-3.5 w-3.5" /> Job
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/** Single implementation — detail.tsx had its own copy until this file. */
export function formatSiteAddress(s: {
    address_line: string | null;
    address_line2: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
}): string {
    const line1 = [s.address_line, s.address_line2].filter(Boolean).join(", ");
    const cityState = [s.city, s.region].filter(Boolean).join(", ");
    const tail = [cityState, s.postal_code].filter(Boolean).join(" ");
    return [line1, tail].filter(Boolean).join(" — ");
}

/**
 * The LOCATION cell shared by the Jobs, Estimates and Invoices tabs.
 *
 * On a national account "where" is the first thing a CSR needs and the last
 * thing these tabs said. The site was already joined in every one of those
 * queries; only its NAME was never selected. An em dash here would repeat the
 * original sin — a record with no location reads as a gap in the data, not a
 * gap in the layout, so it says so.
 */
export function SiteCell({
    siteId,
    siteName,
    city,
}: {
    siteId?: string | null;
    siteName?: string | null;
    city?: string | null;
}) {
    if (!siteName && !siteId) {
        return <span className="text-xs text-foreground/60 italic">No location on this record</span>;
    }
    const label = siteName ?? "(unnamed location)";
    return (
        <div className="space-y-0.5">
            {siteId ? (
                <Link
                    to={`/sites/${siteId}`}
                    className="text-xs text-primary hover:underline"
                    // These cells sit in rows that open their own record on
                    // click; without this the row's click wins and the
                    // location link lands on the job / estimate instead.
                    onClick={(e) => e.stopPropagation()}
                >
                    {label}
                </Link>
            ) : (
                <span className="text-xs">{label}</span>
            )}
            {city && <div className="text-[11px] text-foreground/70">{city}</div>}
        </div>
    );
}
